﻿import { FetchAsyncParams, fetchRequest } from '@tk/utilities/tk.fetch';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import render from '@tk/utilities/tk.render';
import group from '@tk/utilities/tk.array.group';
import showDimmer from '@tk/utilities/tk.dimmer';
import TKFilterGroupWrapper from './tk.filter.group.wrapper';
import TKArticleList from './tk.article.list';
import { toBase64 } from '@tk/utilities/tk.base64';

export type FilterAction = 'STARTED' | 'SENDED' | 'REFRESHED' | 'RESETTED' | 'ENDED' | 'REFRESHED-DRILLDOWN';

export enum FilterType {
    RD_FILTER = 'rd-filter',
    URL_PARAM_FILTER = 'url-param-filter',
    SIMPLE_FILTER = 'simple-filter',
}

export interface FilterItem {
    id: string;
    attr: string;
    value: string;
}

interface DrilldownData {
    id: string;
    values: string;
}

type FilterData = Record<string, string>;
type URLParams = Record<string, string>;

interface PreparedFilter {
    data: FilterData;
    url: string;
}

export interface FilterEvent {
    action: FilterAction;
    filterList: FilterItem[];
}

export default class TKFilter extends TKCustomElementFactory {
    protected observers: ((data: FilterEvent) => void)[] = [];

    type: FilterType;
    groupId: string;
    pageLayout: string;
    filterList: FilterItem[];
    allowURLParamsPrefix: string;
    filterGroupSelectedClassName: string;
    dimmer?: HTMLElement;
    filterURL?: string;

    constructor() {
        super();

        this.type = (this.getAttribute('data-tk-filter-type') as FilterType) || FilterType.URL_PARAM_FILTER;
        this.groupId = this.getAttribute('data-tk-filter-group-id') || 'cat';
        this.pageLayout = this.getAttribute('data-tk-page-layout') || 'asyn';
        this.allowURLParamsPrefix = this.getAttribute('data-tk-allow-url-params-prefix') || 'cf-';
        this.filterGroupSelectedClassName =
            this.getAttribute('data-tk-filter-group-selected-class-name') || 'tk-filter-group--has-selected';
        this.filterList = [];
        this.filterURL = this.getAttribute('data-tk-filter-url') || undefined;
    }

    connectedCallback(): void {
        this.emit({ action: 'STARTED', filterList: this.filterList });
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.emit({ action: 'ENDED', filterList: this.filterList });
    }

    subscribe(listener: (data: FilterEvent) => void) {
        this.observers.push(listener);
    }

    emit(data: FilterEvent) {
        this.observers.forEach((listener) => {
            listener(data);
        });
    }

    add(filterList: FilterItem[], isRange: boolean = false) {
        const property: keyof FilterItem = this.type === FilterType.RD_FILTER ? 'id' : 'attr';
        const selectedProperties = new Set(filterList.map((item) => item[property]));
        selectedProperties.forEach((item) => {
            this.deleteAllByProp(item);
        });
        if (isRange) {
            const filterValues = filterList.map((item) => item.value);
            const filterItem = filterList.at(0);
            if (!filterItem) return;
            const { id, attr } = filterItem;
            this.filterList.push({
                id,
                attr,
                value: TKFilter.convertToMultiValue(filterValues),
            });
        } else {
            filterList.forEach((item) => {
                this.filterList.push(item);
            });
        }
    }

    exist({ id, attr, value }: FilterItem): boolean {
        return this.type === FilterType.RD_FILTER
            ? this.filterList.some((item) => item.id === id && item.value === value)
            : this.filterList.some((item) => item.attr === attr && item.value === TKFilter.neutralizeRangeValue(value));
    }

    delete({ id, attr, value }: FilterItem): void {
        const filteredList = this.filterList.filter(
            (item) => item.id !== id || item.value !== value || item.attr !== attr,
        );
        this.filterList = filteredList;
    }

    deleteAllByProp(value: string) {
        const property: keyof FilterItem = this.type === FilterType.RD_FILTER ? 'id' : 'attr';
        const filteredList = this.filterList.filter((item) => item[property] !== value);
        this.filterList = filteredList;
    }

    deleteAll() {
        this.filterList = [];
        this.emit({ action: 'RESETTED', filterList: this.filterList });
    }

    post() {
        const preparedFilter = this.getPreparedFilter(this.type);
        const request: FetchAsyncParams<TKResponse> = {
            requestURL: preparedFilter.url,
            resolveHandler: this.refreshResultList.bind(this),
        };
        if (this.type === FilterType.SIMPLE_FILTER) request.payload = preparedFilter.data;
        fetchRequest(request);
        this.emit({ action: 'SENDED', filterList: this.filterList });
        if (this.type === FilterType.SIMPLE_FILTER) return;
        !this.filterURL && window.history.replaceState(null, '', preparedFilter.url);
        this.dimmer = showDimmer();
    }

    refreshResultList(response: TKResponse) {
        if (!response || !response.success) return;
        const currentContent = this.querySelector('[data-tk-filter-content]');
        const html = render(response.dataAsHtml, true);
        const newContent = html.querySelector('[data-tk-filter-content]');
        if (!currentContent || !newContent) return;
        currentContent.innerHTML = newContent.innerHTML;
        this.emit({ action: 'REFRESHED', filterList: this.filterList });
        this.dimmer?.remove();
    }

    refreshDrilldown() {
        const groupedData = group(this.filterList, 'id');
        const list: DrilldownData[] = [];
        Object.keys(groupedData).forEach((key) => {
            const values = groupedData[key].map((obj) => obj.value).join(', ');
            list.push({ id: key, values });
        });
        this.resetFilterGroups(list.map((item) => item.id));
        const preparedFilter = this.getPreparedFilter(this.type);
        const request: FetchAsyncParams<TKResponse> = {
            requestURL: preparedFilter.url,
            resolveHandler: this.refreshDrilldownContent.bind(this),
        };
        if (this.type === FilterType.SIMPLE_FILTER) request.payload = preparedFilter.data;
        fetchRequest(request);
        this.emit({ action: 'SENDED', filterList: this.filterList });
        if (this.type === FilterType.SIMPLE_FILTER) return;
        window.history.replaceState(null, '', preparedFilter.url);
    }

    refreshDrilldownContent(response: TKResponse) {
        if (!response || !response.success) return;
        const currentContent = this.querySelector<TKFilterGroupWrapper>('[data-tk-filter-group-wrapper]');
        const html = render(response.dataAsHtml, true);
        const newContent = html.querySelector('[data-tk-filter-group-wrapper]');
        const articleListContent = this.querySelector<TKArticleList>('[data-tk-article-list]');
        const articleListHTML = render(response.dataAsHtml, true);
        const newArticleListContent = articleListHTML.querySelector('[data-tk-article-list]');

        if (!currentContent || !newContent || !articleListContent || !newArticleListContent) return;
        currentContent.innerHTML = newContent.innerHTML;
        articleListContent.innerHTML = newArticleListContent.innerHTML;
        currentContent.sync();
        this.emit({ action: 'REFRESHED-DRILLDOWN', filterList: this.filterList });
    }

    resetFilterGroups(filteredIds: string[]) {
        const filterGroupElements = this.querySelectorAll('[data-tk-filter-group-dropdown]');
        const filterGroupList = Array.from(filterGroupElements);
        const filteredList = filterGroupList.filter(
            (element) => !filteredIds.includes(element.getAttribute('data-tk-filter-group-dropdown') || ''),
        );
        filteredList.forEach((element) => {
            element.classList.remove(this.filterGroupSelectedClassName);
            const selectedElement = element.querySelector('[data-tk-filter-group-selected]');
            if (!selectedElement) return;
            selectedElement.textContent = '';
        });
    }

    getPreparedFilter(type: FilterType): PreparedFilter {
        const handlers: Record<FilterType, () => PreparedFilter> = {
            'rd-filter': this.prepareRDFilter.bind(this),
            'url-param-filter': this.prepareQueryFilter.bind(this),
            'simple-filter': this.prepareSimpleFilter.bind(this),
        };
        return handlers[type]();
    }

    prepareRDFilter(): PreparedFilter {
        const groupedArray = this.getGroupedFilterArrayValue('id');
        const jsonString = JSON.stringify(groupedArray);
        const data: FilterData = {};
        data[this.groupId] = jsonString;
        const urlData: FilterData = {};
        urlData[this.groupId] = toBase64(jsonString);
        const url =
            Object.keys(groupedArray).length > 0
                ? this.mergeURLParamsByData(urlData)
                : this.mergeURLParamsByData(urlData, true);

        return { data, url: url.href };
    }

    prepareQueryFilter(): PreparedFilter {
        const data = this.getGroupedFilter('attr', ',');
        const urlParams = this.getGroupedFilter('attr', '|');
        const url = this.mergeURLParams(urlParams);
        return { data, url: url.href };
    }

    prepareSimpleFilter(): PreparedFilter {
        const groupedArray = group(this.filterList, 'id');
        const jsonString = JSON.stringify(groupedArray);
        const data: FilterData = {};
        data.selection = jsonString;
        const url = TKFilter.mergeURLs(this.filterURL) || new URL(window.location.href);
        url.searchParams.set('pagelayout', this.pageLayout);
        return { data, url: url.href };
    }

    getGroupedFilter(property: keyof FilterItem, separator: string): Record<string, string> {
        const groupedArray = group(this.filterList, property);
        const result: Record<string, string> = {};
        Object.keys(groupedArray).forEach((key) => {
            const values = groupedArray[key].map((obj) => obj.value).join(separator);
            result[key] = values;
        });
        return result;
    }

    getGroupedFilterArrayValue(property: keyof FilterItem): Record<string, string[]> {
        const groupedArray = group(this.filterList, property);
        const result: Record<string, string[]> = {};
        Object.keys(groupedArray).forEach((key) => {
            const values = groupedArray[key].map((obj) => obj.value);
            result[key] = values;
        });
        return result;
    }

    mergeURLParamsByData(data: FilterData, shouldReset: boolean = false): URL {
        const url = TKFilter.mergeURLs(this.filterURL) || new URL(window.location.href);
        const searchParamList = Array.from(url.searchParams);
        const filteredList = searchParamList.filter((item) => !item[0].startsWith(this.groupId));
        const existingParams: URLParams = Object.fromEntries(filteredList);
        const mergedParams: URLParams = shouldReset ? { ...existingParams } : { ...data, ...existingParams };
        url.search = new URLSearchParams(mergedParams).toString();
        return url;
    }

    mergeURLParams(selectedParams: URLParams): URL {
        const url = TKFilter.mergeURLs(this.filterURL) || new URL(window.location.href);
        const allowedList = this.allowURLParamsPrefix.split(',');
        const searchParamList = Array.from(url.searchParams);
        const filteredList = searchParamList.filter((item) =>
            allowedList.every((allowed) => !item[0].startsWith(allowed)),
        );
        const existingParams: URLParams = Object.fromEntries(filteredList);
        const mergedParams: URLParams = { ...selectedParams, ...existingParams };
        url.search = new URLSearchParams(mergedParams).toString();
        return url;
    }

    static convertToMultiValue(multivalues: string[]): string {
        return `[${multivalues.join('|')}]`;
    }

    static neutralizeRangeValue(value: string): string {
        if (value.includes('[') || value.includes(']')) {
            return value.replaceAll('[', '').replaceAll(']', '');
        }
        return value;
    }

    static mergeURLs(relativeURL: string | undefined) {
        if (!relativeURL) return;
        const baseURL = new URL(window.location.href);
        const mergedURL = new URL(relativeURL, baseURL.origin);

        mergedURL.search = baseURL.search;
        return mergedURL;
    }
}
